import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { UserPlus, Mail, Trash2, Shield, Building, Loader2, AlertCircle, CheckCircle, XCircle, Key, CreditCard as Edit2, Eye, EyeOff, LayoutGrid, List, Search } from 'lucide-react';
import type { UserProfile } from '../types';

interface AuthorityUser extends UserProfile {
  reports_count?: number;
  open_reports_count?: number;
  closed_reports_count?: number;
}

interface NewAuthorityForm {
  email: string;
  notification_email: string;
  mp_emails: string;
  press_emails: string;
  local_authority: string;
  password: string;
}

type ViewMode = 'grid' | 'list';

export function AuthorityManagement() {
  const [authorities, setAuthorities] = useState<AuthorityUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [newAuthority, setNewAuthority] = useState<NewAuthorityForm>({
    email: '',
    notification_email: '',
    mp_emails: '',
    press_emails: '',
    local_authority: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resetPasswordId, setResetPasswordId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ email: '', notification_email: '', mp_emails: '', press_emails: '', local_authority: '' });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchAuthorities();
  }, []);

  const fetchAuthorities = async () => {
    try {
      setLoading(true);

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'authority')
        .order('created_at', { ascending: false });

      if (profileError) throw profileError;

      const authoritiesWithStats = await Promise.all(
        (profiles || []).map(async (profile) => {
          const { data: reports } = await supabase
            .from('verge_conditions')
            .select('id, status')
            .eq('local_authority', profile.local_authority);

          const reports_count = reports?.length || 0;
          const open_reports_count = reports?.filter(r => r.status !== 'closed').length || 0;
          const closed_reports_count = reports?.filter(r => r.status === 'closed').length || 0;

          return {
            ...profile,
            reports_count,
            open_reports_count,
            closed_reports_count
          };
        })
      );

      setAuthorities(authoritiesWithStats);
    } catch (error) {
      console.error('Error fetching authorities:', error);
      setError('Failed to load authority users');
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewAuthority({ ...newAuthority, password });
  };

  const handleCreateAuthority = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!newAuthority.email || !newAuthority.local_authority || !newAuthority.password) {
      setError('Login email, local authority name, and password are required');
      return;
    }

    try {
      setCreating(true);

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: newAuthority.email,
        password: newAuthority.password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            role: 'authority',
            local_authority: newAuthority.local_authority
          }
        }
      });

      if (signUpError) throw signUpError;

      if (!authData.user) {
        throw new Error('Failed to create user account');
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          email: newAuthority.email,
          notification_email: newAuthority.notification_email || null,
          mp_emails: newAuthority.mp_emails || null,
          press_emails: newAuthority.press_emails || null,
          role: 'authority',
          local_authority: newAuthority.local_authority,
          ai_model: 'openai',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (profileError) throw profileError;

      await sendWelcomeEmail(newAuthority.email, newAuthority.local_authority, newAuthority.password);

      setSuccess(`Authority account created successfully! Welcome email sent to ${newAuthority.email}`);
      setNewAuthority({ email: '', notification_email: '', mp_emails: '', press_emails: '', local_authority: '', password: '' });
      setShowCreateForm(false);
      fetchAuthorities();
    } catch (error: any) {
      console.error('Error creating authority:', error);
      setError(error.message || 'Failed to create authority account');
    } finally {
      setCreating(false);
    }
  };

  const sendWelcomeEmail = async (email: string, localAuthority: string, password: string) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-authority-welcome`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            local_authority: localAuthority,
            password,
            portal_url: window.location.origin
          })
        }
      );

      if (!response.ok) {
        console.error('Failed to send welcome email:', await response.text());
      }
    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  };

  const handleDeleteAuthority = async (authorityId: string, email: string) => {
    if (!confirm(`Are you sure you want to delete the authority account for ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', authorityId);

      if (error) throw error;

      setSuccess('Authority account deleted successfully');
      fetchAuthorities();
    } catch (error: any) {
      console.error('Error deleting authority:', error);
      setError('Failed to delete authority account');
    }
  };

  const callAdminFunction = async (body: object) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-user-management`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }
    );
    const json = await response.json();
    if (!response.ok) throw new Error(json.error || 'Request failed');
    return json;
  };

  const handleResetPassword = async (authorityId: string) => {
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    try {
      setResettingPassword(true);
      setError(null);

      await callAdminFunction({ action: 'resetPassword', userId: authorityId, password: newPassword });

      setSuccess('Password reset successfully');
      setResetPasswordId(null);
      setNewPassword('');
      setShowNewPassword(false);
    } catch (error: any) {
      console.error('Error resetting password:', error);
      setError('Failed to reset password: ' + (error.message || 'Unknown error'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleEditAuthority = async (authorityId: string) => {
    try {
      setError(null);

      await callAdminFunction({
        action: 'updateProfile',
        userId: authorityId,
        email: editForm.email,
        notification_email: editForm.notification_email,
        mp_emails: editForm.mp_emails,
        press_emails: editForm.press_emails,
        local_authority: editForm.local_authority,
      });

      setSuccess('Authority account updated successfully');
      setEditingId(null);
      fetchAuthorities();
    } catch (error: any) {
      console.error('Error updating authority:', error);
      setError('Failed to update authority account: ' + (error.message || 'Unknown error'));
    }
  };

  const startEdit = (authority: AuthorityUser) => {
    setEditingId(authority.id);
    setEditForm({
      email: authority.email,
      notification_email: authority.notification_email || '',
      mp_emails: authority.mp_emails || '',
      press_emails: authority.press_emails || '',
      local_authority: authority.local_authority || ''
    });
  };

  const generateNewPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
  };

  const filteredAuthorities = search.trim()
    ? authorities.filter(a => {
        const q = search.toLowerCase();
        return (
          a.local_authority?.toLowerCase().includes(q) ||
          a.email?.toLowerCase().includes(q) ||
          a.notification_email?.toLowerCase().includes(q)
        );
      })
    : authorities;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Shield className="w-6 h-6" />
            Authority User Management
          </h2>
          <p className="text-gray-600 mt-1">
            Create and manage authority user accounts for different local authorities
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              Create Authority Account
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <XCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {showCreateForm && (
        <CreateAuthorityForm
          newAuthority={newAuthority}
          setNewAuthority={setNewAuthority}
          creating={creating}
          onSubmit={handleCreateAuthority}
          onCancel={() => setShowCreateForm(false)}
          onGeneratePassword={generatePassword}
        />
      )}

      {!loading && authorities.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by authority name or email..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : authorities.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No authority accounts created yet</p>
          <p className="text-sm text-gray-500 mt-1">Create your first authority account to get started</p>
        </div>
      ) : filteredAuthorities.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Search className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No authorities match your search</p>
          <button onClick={() => setSearch('')} className="text-sm text-blue-500 hover:text-blue-700 mt-1">Clear search</button>
        </div>
      ) : viewMode === 'grid' ? (
        <GridView
          authorities={filteredAuthorities}
          editingId={editingId}
          editForm={editForm}
          setEditForm={setEditForm}
          resetPasswordId={resetPasswordId}
          newPassword={newPassword}
          showNewPassword={showNewPassword}
          resettingPassword={resettingPassword}
          onStartEdit={startEdit}
          onCancelEdit={() => setEditingId(null)}
          onSaveEdit={handleEditAuthority}
          onResetPassword={handleResetPassword}
          onSetResetPasswordId={setResetPasswordId}
          onSetNewPassword={setNewPassword}
          onSetShowNewPassword={setShowNewPassword}
          onGenerateNewPassword={generateNewPassword}
          onDelete={handleDeleteAuthority}
        />
      ) : (
        <ListView
          authorities={filteredAuthorities}
          editingId={editingId}
          editForm={editForm}
          setEditForm={setEditForm}
          resetPasswordId={resetPasswordId}
          newPassword={newPassword}
          showNewPassword={showNewPassword}
          resettingPassword={resettingPassword}
          onStartEdit={startEdit}
          onCancelEdit={() => setEditingId(null)}
          onSaveEdit={handleEditAuthority}
          onResetPassword={handleResetPassword}
          onSetResetPasswordId={setResetPasswordId}
          onSetNewPassword={setNewPassword}
          onSetShowNewPassword={setShowNewPassword}
          onGenerateNewPassword={generateNewPassword}
          onDelete={handleDeleteAuthority}
        />
      )}
    </div>
  );
}

function CreateAuthorityForm({
  newAuthority,
  setNewAuthority,
  creating,
  onSubmit,
  onCancel,
  onGeneratePassword
}: {
  newAuthority: NewAuthorityForm;
  setNewAuthority: (v: NewAuthorityForm) => void;
  creating: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  onGeneratePassword: () => void;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">Create New Authority Account</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <XCircle className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Login Email *
            </label>
            <input
              type="email"
              value={newAuthority.email}
              onChange={(e) => setNewAuthority({ ...newAuthority, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="login@authority.gov.uk"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Used to sign in to the portal</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notification Email
            </label>
            <input
              type="email"
              value={newAuthority.notification_email}
              onChange={(e) => setNewAuthority({ ...newAuthority, notification_email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="reports@authority.gov.uk"
            />
            <p className="text-xs text-gray-500 mt-1">Receives report notifications and chasers (defaults to login email if blank)</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Local Authority Name *
          </label>
          <input
            type="text"
            value={newAuthority.local_authority}
            onChange={(e) => setNewAuthority({ ...newAuthority, local_authority: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Westminster City Council"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            This must match the local_authority value in your reports
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MP Email Addresses
            </label>
            <input
              type="text"
              value={newAuthority.mp_emails}
              onChange={(e) => setNewAuthority({ ...newAuthority, mp_emails: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="mp1@parliament.uk, mp2@parliament.uk"
            />
            <p className="text-xs text-gray-500 mt-1">Comma-separated. CC'd on Chaser #2 escalation emails.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Local Press Email Addresses
            </label>
            <input
              type="text"
              value={newAuthority.press_emails}
              onChange={(e) => setNewAuthority({ ...newAuthority, press_emails: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="news@localpress.co.uk, editor@gazette.co.uk"
            />
            <p className="text-xs text-gray-500 mt-1">Comma-separated. CC'd on Chaser #2 escalation emails.</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Initial Password *
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={newAuthority.password}
              onChange={(e) => setNewAuthority({ ...newAuthority, password: e.target.value })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              placeholder="Enter or generate password"
              required
            />
            <button
              type="button"
              onClick={onGeneratePassword}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Generate
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            This password will be sent to the authority via email
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">What happens next:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>A user account will be created with the 'authority' role</li>
                <li>A welcome email will be sent with login credentials</li>
                <li>The authority will only see reports from their jurisdiction</li>
                <li>They can comment on and close reports</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            disabled={creating}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={creating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                Create Account & Send Email
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

interface EditFormState {
  email: string;
  notification_email: string;
  mp_emails: string;
  press_emails: string;
  local_authority: string;
}

interface ViewProps {
  authorities: AuthorityUser[];
  editingId: string | null;
  editForm: EditFormState;
  setEditForm: (v: EditFormState) => void;
  resetPasswordId: string | null;
  newPassword: string;
  showNewPassword: boolean;
  resettingPassword: boolean;
  onStartEdit: (a: AuthorityUser) => void;
  onCancelEdit: () => void;
  onSaveEdit: (id: string) => void;
  onResetPassword: (id: string) => void;
  onSetResetPasswordId: (id: string | null) => void;
  onSetNewPassword: (v: string) => void;
  onSetShowNewPassword: (v: boolean) => void;
  onGenerateNewPassword: () => void;
  onDelete: (id: string, email: string) => void;
}

function EditFormInline({ editForm, setEditForm, onCancel, onSave, authorityId }: {
  editForm: EditFormState;
  setEditForm: (v: EditFormState) => void;
  onCancel: () => void;
  onSave: (id: string) => void;
  authorityId: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-gray-800">Edit Authority</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <XCircle className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Login Email</label>
          <input
            type="email"
            value={editForm.email}
            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Notification Email</label>
          <input
            type="email"
            value={editForm.notification_email}
            onChange={(e) => setEditForm({ ...editForm, notification_email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Defaults to login email if blank"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Local Authority</label>
        <input
          type="text"
          value={editForm.local_authority}
          onChange={(e) => setEditForm({ ...editForm, local_authority: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">MP Email Addresses</label>
        <input
          type="text"
          value={editForm.mp_emails}
          onChange={(e) => setEditForm({ ...editForm, mp_emails: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="mp1@parliament.uk, mp2@parliament.uk"
        />
        <p className="text-xs text-gray-400 mt-0.5">Comma-separated. CC'd on Chaser #2 emails.</p>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Local Press Email Addresses</label>
        <input
          type="text"
          value={editForm.press_emails}
          onChange={(e) => setEditForm({ ...editForm, press_emails: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          placeholder="news@localpress.co.uk, editor@gazette.co.uk"
        />
        <p className="text-xs text-gray-400 mt-0.5">Comma-separated. CC'd on Chaser #2 emails.</p>
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
          Cancel
        </button>
        <button onClick={() => onSave(authorityId)} className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm">
          Save Changes
        </button>
      </div>
    </div>
  );
}

function ResetPasswordInline({ authority, newPassword, showNewPassword, resettingPassword, onCancel, onReset, onSetNewPassword, onSetShowNewPassword, onGenerate }: {
  authority: AuthorityUser;
  newPassword: string;
  showNewPassword: boolean;
  resettingPassword: boolean;
  onCancel: () => void;
  onReset: (id: string) => void;
  onSetNewPassword: (v: string) => void;
  onSetShowNewPassword: (v: boolean) => void;
  onGenerate: () => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-gray-800">Reset Password</h3>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
          <XCircle className="w-4 h-4" />
        </button>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          New Password for {authority.email}
        </label>
        <div className="flex gap-2 mb-2">
          <div className="relative flex-1">
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => onSetNewPassword(e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter new password"
            />
            <button
              type="button"
              onClick={() => onSetShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <button onClick={onGenerate} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm">
            Generate
          </button>
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm" disabled={resettingPassword}>
          Cancel
        </button>
        <button
          onClick={() => onReset(authority.id)}
          disabled={resettingPassword || !newPassword}
          className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
        >
          {resettingPassword ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Resetting...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </div>
    </div>
  );
}

function ActionButtons({ authority, onStartEdit, onSetResetPasswordId, onDelete }: {
  authority: AuthorityUser;
  onStartEdit: (a: AuthorityUser) => void;
  onSetResetPasswordId: (id: string) => void;
  onDelete: (id: string, email: string) => void;
}) {
  return (
    <div className="flex gap-2">
      <button onClick={() => onStartEdit(authority)} className="text-blue-500 hover:text-blue-700 transition-colors" title="Edit authority">
        <Edit2 className="w-4 h-4" />
      </button>
      <button onClick={() => onSetResetPasswordId(authority.id)} className="text-orange-500 hover:text-orange-700 transition-colors" title="Reset password">
        <Key className="w-4 h-4" />
      </button>
      <button onClick={() => onDelete(authority.id, authority.email)} className="text-red-500 hover:text-red-700 transition-colors" title="Delete authority account">
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

function GridView(props: ViewProps) {
  const {
    authorities, editingId, editForm, setEditForm,
    resetPasswordId, newPassword, showNewPassword, resettingPassword,
    onStartEdit, onCancelEdit, onSaveEdit,
    onResetPassword, onSetResetPasswordId, onSetNewPassword, onSetShowNewPassword, onGenerateNewPassword,
    onDelete
  } = props;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {authorities.map((authority) => (
        <div
          key={authority.id}
          className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow"
        >
          {editingId === authority.id ? (
            <EditFormInline
              editForm={editForm}
              setEditForm={setEditForm}
              onCancel={onCancelEdit}
              onSave={onSaveEdit}
              authorityId={authority.id}
            />
          ) : resetPasswordId === authority.id ? (
            <ResetPasswordInline
              authority={authority}
              newPassword={newPassword}
              showNewPassword={showNewPassword}
              resettingPassword={resettingPassword}
              onCancel={() => {
                onSetResetPasswordId(null);
                onSetNewPassword('');
                onSetShowNewPassword(false);
              }}
              onReset={onResetPassword}
              onSetNewPassword={onSetNewPassword}
              onSetShowNewPassword={onSetShowNewPassword}
              onGenerate={onGenerateNewPassword}
            />
          ) : (
            <>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Building className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{authority.local_authority}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Key className="w-3 h-3" />
                      <span>{authority.email}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Mail className="w-3 h-3" />
                      <span>{authority.notification_email || authority.email}</span>
                      {!authority.notification_email && <span className="text-xs text-gray-400">(same as login)</span>}
                    </div>
                  </div>
                </div>
                <ActionButtons
                  authority={authority}
                  onStartEdit={onStartEdit}
                  onSetResetPasswordId={onSetResetPasswordId}
                  onDelete={onDelete}
                />
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <div className="text-2xl font-bold text-gray-800">{authority.reports_count || 0}</div>
                  <div className="text-xs text-gray-600">Total Reports</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-orange-600">{authority.open_reports_count || 0}</div>
                  <div className="text-xs text-gray-600">Open</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{authority.closed_reports_count || 0}</div>
                  <div className="text-xs text-gray-600">Closed</div>
                </div>
              </div>

              <div className="mt-4 text-xs text-gray-500">
                Created {new Date(authority.created_at).toLocaleDateString()}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function ListView(props: ViewProps) {
  const {
    authorities, editingId, editForm, setEditForm,
    resetPasswordId, newPassword, showNewPassword, resettingPassword,
    onStartEdit, onCancelEdit, onSaveEdit,
    onResetPassword, onSetResetPasswordId, onSetNewPassword, onSetShowNewPassword, onGenerateNewPassword,
    onDelete
  } = props;

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Authority</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notification Email</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Open</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Closed</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {authorities.map((authority) => (
              <React.Fragment key={authority.id}>
                {editingId === authority.id ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4">
                      <EditFormInline
                        editForm={editForm}
                        setEditForm={setEditForm}
                        onCancel={onCancelEdit}
                        onSave={onSaveEdit}
                        authorityId={authority.id}
                      />
                    </td>
                  </tr>
                ) : resetPasswordId === authority.id ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-4">
                      <ResetPasswordInline
                        authority={authority}
                        newPassword={newPassword}
                        showNewPassword={showNewPassword}
                        resettingPassword={resettingPassword}
                        onCancel={() => {
                          onSetResetPasswordId(null);
                          onSetNewPassword('');
                          onSetShowNewPassword(false);
                        }}
                        onReset={onResetPassword}
                        onSetNewPassword={onSetNewPassword}
                        onSetShowNewPassword={onSetShowNewPassword}
                        onGenerate={onGenerateNewPassword}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900">{authority.local_authority}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{authority.email}</td>
                    <td className="px-4 py-3 text-sm">
                      {authority.notification_email ? (
                        <span className="text-gray-600">{authority.notification_email}</span>
                      ) : (
                        <span className="text-gray-400 italic">Same as login</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center font-medium text-gray-800">{authority.reports_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-center font-medium text-orange-600">{authority.open_reports_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-center font-medium text-green-600">{authority.closed_reports_count || 0}</td>
                    <td className="px-4 py-3 text-sm text-center text-gray-500">{new Date(authority.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <ActionButtons
                        authority={authority}
                        onStartEdit={onStartEdit}
                        onSetResetPasswordId={onSetResetPasswordId}
                        onDelete={onDelete}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
